<template>
    <div :key="defaultKey">
        <validation-observer ref="createRegistrableControllerValidation">
            <validation-provider
                v-slot="{ errors }"
                name="shareholder type"
                rules="required"
                class="end-align-indicator-long-select"
            >
                <b-form-group
                    label="Shareholder type"
                    :state="errors.length > 0 ? false : null"
                >
                    <v-select
                        ref="shareholderType"
                        key="shareholder-type"
                        v-model="registrableController.shareholderType"
                        placeholder="Select shareholder type"
                        class="custom-select-icon text-capitalize"
                        :clearable="false"
                        label="text"
                        :options="shareholderOptions"
                        @input="onShareTypeChange"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                :rules="{ required: isIdRequired }"
                :name="dynamicIdLabel"
            >
                <b-form-group>
                    <slot name="label">
                        <label id="registrable-controller-id" for="id">
                            {{ dynamicIdLabel }}
                            <vector
                                v-if="isRegistrableControllerTypeIndividual"
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-form-input
                        :key="dynamicIdLabel"
                        v-model="registrableController.NRIC"
                        :placeholder="dynamicIdPlaceholder"
                        :state="errors.length > 0 ? false : null"
                        data-test="registrable-controller-id"
                        @input="requestAutoFillFormData"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>

                    <b-popover
                        v-if="isRegistrableControllerTypeIndividual"
                        target="registrable-controller-id"
                        triggers="hover"
                        placement="right"
                        variant="black"
                    >
                        Enter your NRIC or FIN if you are a Singapore resident.
                        <br />
                        Enter your passport number if you are a foreigner.
                    </b-popover>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="legal name"
                rules="required"
            >
                <b-form-group label="Legal name">
                    <b-form-input
                        id="name"
                        key="legal-name"
                        v-model="registrableController.name"
                        placeholder="Enter registrable controller name"
                        :state="errors.length > 0 ? false : null"
                        data-test="registrable-controller-name"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>
            
            <div v-if="isRegistrableControllerTypeIndividual">
                <validation-provider
                    v-slot="{ errors }"
                    name="ID type"
                    rules="required"
                    class="end-align-indicator-long-select"
                >
                    <b-form-group
                        label="ID type"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="idType"
                            key="id-type"
                            v-model="registrableController.idType"
                            class="custom-select-icon"
                            :clearable="false"
                            placeholder="Select ID type"
                            label="name"
                            :options="idType_options"
                            @input="handleNationalityOnIdTypeChanged"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    v-slot="{ errors }"
                    name="nationality"
                    rules="required"
                    class="end-align-indicator-long-select"
                    mode="lazy"
                >
                    <b-form-group
                        label="Nationality"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="nationality"
                            key="nationality"
                            v-model="registrableController.nationality"
                            class="custom-select-icon"
                            :clearable="false"
                            placeholder="Select nationality"
                            label="text"
                            :disabled ="shouldDisabledNationality"
                            :options="nationality_options"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    v-if="shouldShowExpiryDate"
                    v-slot="{ errors }"
                    name="date of expiry"
                    rules="required"
                >
                    <b-form-group :state="errors.length > 0 ? false : null">
                        <slot name="label">
                            <label id="dateOfExpiry" for="id">
                                Date of expiry
                                <vector
                                    data-type="icon"
                                    data-width="15px"
                                    data-height="15px"
                                    data-class="mr-.5"
                                    data-name="information"
                                ></vector>
                            </label>
                        </slot>

                        <b-popover
                            target="dateOfExpiry"
                            triggers="hover"
                            placement="right"
                            variant="black"
                        >
                            Skip this if you are a Singapore citizen or permanent
                            resident.
                            <br />
                            Fill in the date of expiry of your passport or FIN if
                            you are a foreigner.
                        </b-popover>

                        <flat-pickr
                            v-model="registrableController.dateOfExpiry"
                            placeholder="Enter date of expiry"
                            class="form-control"
                            :config="datePickerConfig"
                            data-test="date-picker-doe"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider v-slot="{ errors }" name="alias">
                    <b-form-group label="Alias">
                        <b-form-input
                            id="alias"
                            key="alias"
                            v-model="registrableController.alias"
                            placeholder="Enter registrable controller alias"
                            :state="errors.length > 0 ? false : null"
                            data-test="registrable-controller-alias"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>
            </div>

            <div v-if="! isRegistrableControllerTypeIndividual">
                <validation-provider
                    v-slot="{ errors }"
                    name="country of incorporation"
                    rules="required"
                    class="end-align-indicator-long-select"
                    mode="lazy"
                >
                    <b-form-group
                        label="Country of incorporation"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="country"
                            key="country"
                            v-model="registrableController.country"
                            class="custom-select-icon"
                            :clearable="false"
                            placeholder="Select country of incorporation"
                            label="text"
                            :options="country_options"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>
            </div>

            <validation-provider
                v-slot="{ errors }"
                :name="dynamicDobLabel.toLowerCase()"
                rules="required"
            >
                <b-form-group
                    :label="dynamicDobLabel"
                    :state="errors.length > 0 ? false : null"
                >
                    <flat-pickr
                        key="dob"
                        v-model="registrableController.dob"
                        :placeholder="dynamicDobPlaceholder"
                        class="form-control"
                        :config="datePickerConfig"
                        data-test="date-picker-dob"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                :name="dynamicEmailLabel.toLowerCase()"
                rules="required|email"
            >
                <b-form-group :label="dynamicEmailLabel">
                    <b-form-input
                        :key="dynamicEmailLabel"
                        v-model="registrableController.email"
                        :placeholder="dynamicEmailPlaceholder"
                        :state="errors.length > 0 ? false : null"
                        data-test="registrable-controller-email"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="contact number"
                rules="required"
            >
                <b-form-group label="Contact number">
                    <b-form-input
                        key="contact-number"
                        v-model="registrableController.contactNumber"
                        placeholder="Enter registrable controller contact number"
                        :state="errors.length > 0 ? false : null"
                        data-test="registrable-controller-contact_number"
                        type="tel"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="date of becoming registrable controller"
                rules="required"
            >
                <b-form-group
                    :label="dynamicDateOfRegistrableControllerBecameLabel"
                    :state="errors.length > 0 ? false : null"
                >
                    <flat-pickr
                        key="become-on"
                        v-model="registrableController.become_on"
                        placeholder="Enter date of becoming controller"
                        class="form-control"
                        :config="datePickerConfig"
                        data-test="date-picker-becoming"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="notice sent on"
            >
                <b-form-group
                    label="Notice sent on"
                    :state="errors.length > 0 ? false : null"
                >
                    <flat-pickr
                        key="sent-on"
                        v-model="registrableController.sent_on"
                        placeholder="Enter date of notice send on"
                        class="form-control"
                        :config="datePickerConfig"
                        data-test="date-picker-sent-on"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="confirmation received on"
            >
                <b-form-group
                    label="Confirmation received on"
                    :state="errors.length > 0 ? false : null"
                >
                    <flat-pickr
                        key="received-on"
                        v-model="registrableController.received_on"
                        placeholder="Enter date of confirmation received on"
                        class="form-control"
                        :config="datePickerConfig"
                        data-test="date-picker-received-on"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <validation-provider v-slot="{ errors }" name="cessation date">
                <b-form-group
                    :label="dynamicDateOfRegistrableControllerCeasedLabel"
                    :state="errors.length > 0 ? false : null"
                >
                    <flat-pickr
                        key="cessation-date"
                        v-model="registrableController.cessation_date"
                        placeholder="Enter cessation date"
                        class="form-control"
                        :config="datePickerConfig"
                        data-test="date-picker-cessation-date"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>

            <!-- < CORPORATE NOMINATOR > -->
            <div v-if="!isRegistrableControllerTypeIndividual">
                <validation-provider
                    v-slot="{ errors }"
                    name="corporate registrable controller UEN"
                    rules="required"
                >
                    <b-form-group label="UEN issued by the registrar (ACRA)">
                        <b-form-input
                            id="name"
                            key="uen"
                            v-model="registrableController.uen"
                            placeholder="Enter registrable controller UEN"
                            :state="errors.length > 0 ? false : null"
                            data-test="registrable-controller-uen"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    v-slot="{ errors }"
                    name="registrable controller legal form"
                    rules="required"
                    class="end-align-indicator-long-select"
                >
                    <b-form-group
                        label="Legal form of the registrable corporate controller"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="legalForm"
                            key="legal-form"
                            v-model="registrableController.legalForm"
                            placeholder="Select registrable controller legal form"
                            class="custom-select-icon text-capitalize"
                            :clearable="false"
                            label="text"
                            :options="legalForms"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    v-slot="{ errors }"
                    name="registrable controller jurisdiction"
                    rules="required"
                    class="end-align-indicator-long-select"
                >
                    <b-form-group
                        label="Jurisdiction where the registrable corporate controller is formed or incorporated"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="jurisdiction"
                            key="jurisdiction"
                            v-model="registrableController.jurisdiction"
                            :clearable="false"
                            :options="jurisdictions"
                            label="name"
                            value="id"
                            class="custom-select-icon text-capitalize"
                            placeholder="Select registrable controller jurisdiction"
                            @input="onNominatorJurisdictionChange"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    v-slot="{ errors }"
                    name="registrable controller statue"
                    rules="required"
                    class="end-align-indicator-long-select"
                >
                    <b-form-group
                        label="Statue under which the registrable corporate controller is formed or incorporated"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="statue"
                            key="statue"
                            v-model="registrableController.statue"
                            placeholder="Select registrable controller statue"
                            class="custom-select-icon text-capitalize"
                            :clearable="false"
                            label="name"
                            value="id"
                            :options="statues"
                            :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>

                <validation-provider
                    v-slot="{ errors }"
                    name="registrar"
                    rules="required"
                    class="end-align-indicator-long-select"
                >
                    <b-form-group
                        label="Name of the corporate entity register of the jurisdiction where the registrable corporate controller is formed or incorporated, if applicable"
                        :state="errors.length > 0 ? false : null"
                    >
                        <v-select
                            ref="registrar"
                            key="registrar"
                            v-model="registrableController.registrar"
                            class="custom-select-icon text-capitalize"
                            :clearable="false"
                            label="name"
                            value="id"
                            :options="registrars"
                            :state="errors.length > 0 ? false : null"
                            placeholder="Select corporate registrar"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                </validation-provider>
            </div>
            <!-- < /CORPORATE NOMINATOR > -->

            <validation-provider
                v-slot="{ errors }"
                :name="dynamicAddressLabel.toLowerCase()"
                rules="required"
            >
                <b-form-group>
                    <slot name="label">
                        <label id="address" for="id">
                            {{ dynamicAddressLabel }}
                            <vector
                                v-if="
                                    !registrableController.shareholderType ||
                                    registrableController.shareholderType
                                        .text !== CORPORATE_TYPE
                                "
                                data-type="icon"
                                data-width="15px"
                                data-height="15px"
                                data-class="mr-.5"
                                data-name="information"
                            ></vector>
                        </label>
                    </slot>

                    <b-form-textarea
                        id="textarea"
                        key="textarea"
                        v-model="registrableController.address"
                        :placeholder="dynamicAddressPlaceholder"
                        :state="errors.length > 0 ? false : null"
                        data-test="registrable-controller-address"
                        rows="3"
                        max-rows="3"
                    >
                    </b-form-textarea>
                    <small class="text-danger">{{ errors[0] }}</small>

                    <b-popover
                        v-if="
                            !registrableController.shareholderType ||
                            registrableController.shareholderType.text !==
                                CORPORATE_TYPE
                        "
                        target="address"
                        triggers="hover"
                        placement="right"
                        variant="black"
                    >
                        Enter Singapore address if you are a Singapore resident.
                        <br />
                        Enter your overseas address if you are a foreigner.
                    </b-popover>
                </b-form-group>
            </validation-provider>

            <validation-provider
                v-slot="{ errors }"
                name="remark"
            >
                <b-form-group label="Remarks">
                    <b-form-input
                        id="remark"
                        v-model="registrableController.remark"
                        placeholder="Enter registrable controller remark"
                        :state="errors.length > 0 ? false : null"
                        data-test="remark"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
            </validation-provider>
        </validation-observer>

        <b-button
            block
            class="mt-2"
            variant="primary"
            data-test="create-registrable-controller-btn"
            @click="createOrUpdate"
            >{{ status === "create" ? "Add" : "Update" }}
            <img
                v-if="status === 'create'"
                :src="'/illustrations/add-circle.svg'"
                class="float-right"
                alt="add-logo"
            />
            <img
                v-if="status === 'update'"
                :src="'/illustrations/edit.svg'"
                class="float-right"
                alt="edit-logo"
            />
        </b-button>

        <b-button
            block
            class="mb-3"
            variant="outline-primary"
            @click="$emit('cancel')"
            >Cancel
            <vector
                data-type="icons"
                data-width="15px"
                data-height="15px"
                data-name="cancel"
                class="float-right"
                alt="cancel-logo"
            />
        </b-button>
    </div>
</template>

<script>
import {
    BFormGroup,
    BFormInput,
    BButton,
    BFormTextarea,
    BPopover,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import { debounce } from 'debounce';
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import Nationality from "@/models/Nationality";
import Nominator from "@/models/Nominator";
import IdType from "@/models/IdType";
import Country from "@/models/Country";
import User from '@/models/User';
import { SHAREHOLDER, NRIC_CITIZEN_ID, SINGAPORE_CITIZEN } from "@/constants/config";
import { formatDate } from "@/helpers";

export default {
    components: {
        BFormGroup,
        BFormInput,
        BButton,
        ValidationObserver,
        ValidationProvider,
        flatPickr,
        vSelect,
        BFormTextarea,
        BPopover,
    },

    props: {
        dataStatus: {
            required: false,
            default: "create",
            type: String,
        },

        dataRegistrableController: {
            required: true,
            type: Object,
        },
    },

    data() {
        return {
            idTips: "Tips For Id",
            required,
            email,
            defaultKey: 0,
            status: this.dataStatus,
            CORPORATE_TYPE: SHAREHOLDER.TYPE.CORPORATE,
            INDIVIDUAL_TYPE: SHAREHOLDER.TYPE.INDIVIDUAL,

            registrableController: {
                // eslint-disable-next-line no-underscore-dangle
                _id: this.dataRegistrableController._id,
                name: this.dataRegistrableController.name,
                alias: this.dataRegistrableController.alias,
                idType: this.dataRegistrableController?.idType,
                NRIC: this.dataRegistrableController.NRIC,
                shareholderType: this.dataRegistrableController.shareholderType,
                nationality: this.dataRegistrableController.nationality,
                dateOfExpiry: this.dataRegistrableController.dateOfExpiry,
                country: this.dataRegistrableController.country,
                dob: this.dataRegistrableController.dob,
                email: this.dataRegistrableController.email,
                contactNumber: this.dataRegistrableController.contactNumber,
                become_on: this.dataRegistrableController.become_on,
                sent_on: this.dataRegistrableController.sent_on,
                received_on: this.dataRegistrableController.received_on,
                cessation_date: this.dataRegistrableController.cessation_date,
                remark: this.dataRegistrableController.remark,
                uen: this.dataRegistrableController.uen,
                legalForm: this.dataRegistrableController.legalForm,
                jurisdiction: this.dataRegistrableController.jurisdiction,
                statue: this.dataRegistrableController.statue,
                registrar: this.dataRegistrableController.registrar,
                address: this.dataRegistrableController.address,
            },

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            nationality_options: [],
            country_options: [],

            shareholderOptions: [
                {
                    text: SHAREHOLDER.TYPE.INDIVIDUAL,
                    value: SHAREHOLDER.TYPE.INDIVIDUAL,
                },
                {
                    text: SHAREHOLDER.TYPE.CORPORATE,
                    value: SHAREHOLDER.TYPE.CORPORATE,
                },
            ],

            statues: [],
            dataStatues: [],
            registrars: [],
            dataRegistrars: [],
            legalForms: [],
            jurisdictions: [],
            idType_options: [],
        };
    },

    computed: {
        isRegistrableControllerTypeIndividual() 
        {
            // if there is no type yet, act like it's Individual
            if (!this.registrableController.shareholderType) return true;

            return (
                this.registrableController.shareholderType &&
                this.registrableController.shareholderType.text ===
                    SHAREHOLDER.TYPE.INDIVIDUAL
            );
        },

        isRegistrableControllerTypeCorporate() 
        {
            return this.registrableController.shareholderType?.text === SHAREHOLDER.TYPE.CORPORATE;
        },

        isIdRequired() 
        {
            let type = this.registrableController.shareholderType;

            if (!type) return true;

            if (type && typeof type !== "string") type = type.text;

            return type === SHAREHOLDER.TYPE.INDIVIDUAL;
        },

        dynamicIdLabel() {
            if (this.isRegistrableControllerTypeCorporate)
                return "Identification number or registration number of the registrable corporate controller on the corporate entity register of the jurisdiction where the registrable corporate controller is formed or incorporated, if applicable";

            return "ID";
        },

        dynamicIdPlaceholder() {
            if (this.isRegistrableControllerTypeCorporate)
                return "Enter registration number";

            return "Enter NRIC or passport";
        },

        dynamicNationalityLabel() {
            if (this.isRegistrableControllerTypeCorporate)
                return "Country of incorporation";

            return "Nationality";
        },

        dynamicNationalityPlaceholder() {
            if (this.isRegistrableControllerTypeCorporate)
                return "Select country of incorporation";

            return "Select nationality";
        },

        dynamicDobLabel() {
            if (this.isRegistrableControllerTypeCorporate)
                return "Incorporation date";

            return "Date of birth";
        },

        dynamicDobPlaceholder() {
            if (this.isRegistrableControllerTypeCorporate)
                return "Enter incorporation date";

            return "Enter date of birth";
        },

        dynamicEmailLabel() {
            if (this.isRegistrableControllerTypeCorporate)
                return "Corporate representative email";

            return "Email";
        },

        dynamicEmailPlaceholder() {
            if (this.isRegistrableControllerTypeCorporate)
                return "Enter corporate representative email";

            return "Enter email";
        },

        dynamicAddressLabel() {
            if (this.isRegistrableControllerTypeCorporate)
                return "Address of registered office";

            return "Address";
        },

        dynamicAddressPlaceholder() {
            if (this.isRegistrableControllerTypeCorporate)
                return "Enter address of registered office";

            return "Enter registrable controller address";
        },

        dynamicDateOfRegistrableControllerBecameLabel() {
            if (this.isRegistrableControllerTypeCorporate) 
                return "Date on which the registrable corporate controller became a corporate controller of the company";

            return "Date on which the registrable individual controller became an individual controller of the company";
        },

        dynamicDateOfRegistrableControllerCeasedLabel() {
            if (this.isRegistrableControllerTypeCorporate) 
                return "Date on which the registrable corporate controller ceased to be a corporate controller of the company, if applicable";

            return "Date on which the registrable individual controller ceased to be an individual controller of the company, if applicable";
        },

        shouldDisabledNationality() {
            if(this.registrableController.idType && this.registrableController.idType.id === NRIC_CITIZEN_ID)
                return true;
            return false; 
        },

        shouldShowExpiryDate()
        {
            if(this.registrableController.idType?.name.includes("NRIC")) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.registrableController.dateOfExpiry = null;
            }
            return ! this.registrableController.idType?.name.includes("NRIC");
        },
    },

    async created() {
        let shareholderType = SHAREHOLDER.TYPE.INDIVIDUAL;

        if (
            this.registrableController.shareholderType?.text?.toLowerCase()?.includes("corporate")
        ) shareholderType = SHAREHOLDER.TYPE.CORPORATE;

        if (
            this.registrableController.shareholderType?.text?.toLowerCase()?.includes("individual")
        ) shareholderType = SHAREHOLDER.TYPE.INDIVIDUAL;

        // this.registrableController.shareholderType ? this.registrableController.shareholderType.text : SHAREHOLDER.TYPE.INDIVIDUAL
        this.reInitializeNominators(shareholderType);

        const payload = await Nominator.getCorporateData();

        this.jurisdictions = payload.jurisdiction;
        this.statues = payload.statue;
        this.dataStatues = this.statues;
        this.legalForms = payload.legal_form;
        this.registrars = payload.registrar;
        this.dataRegistrars = this.registrars;

        this.idType_options = await IdType.all();
        const nationalityResponse = await Nationality.all();
        const countryResponse = await Country.all();

        this.nationality_options = nationalityResponse.map(obj => {
            const value = obj.id;
            const text = obj.name;
            return { value, text };
        });

        this.country_options = countryResponse.data.map(obj => {
            const value = obj.id;
            const text = obj.name;
            return { value, text };
        });

        if (this.registrableController.country) {
            // eslint-disable-next-line prefer-destructuring
            this.registrableController.country = this.country_options.filter(
                opt => opt.value === this.registrableController.country
            )[0];
        }
    },

    methods: {
        reInitializeNominators(type) {
            if (type === "individual") {
                this.registrableController.uen = undefined;
                this.registrableController.jurisdiction = undefined;
                this.registrableController.legalForm = undefined;
                this.registrableController.statue = undefined;
                this.registrableController.registrar = undefined;
            }
        },

        async onShareTypeChange(payload) {
            this.defaultKey += 1;
            this.$refs.createRegistrableControllerValidation.reset();
            this.reInitializeNominators(payload.value);

            if (this.isRegistrableControllerTypeIndividual) {
                this.registrableController.country = undefined;
                
                this.registrableController.idType = this.dataRegistrableController.idType;
                this.registrableController.nationality = this.dataRegistrableController.nationality;
                
            } else {
                this.registrableController.idType = undefined;
                this.registrableController.nationality = undefined;
            }
        },

        onNominatorJurisdictionChange(payload) {
            this.statues = this.dataStatues.filter(
                statue => statue.jurisdiction_id === payload.id
            );
            this.registrars = this.dataRegistrars.filter(
                registrar => registrar.jurisdiction_id === payload.id
            );
            this.registrableController.statue = "";
            this.registrableController.registrar = "";
        },

        handleNationalityOnIdTypeChanged(data) {
            if(data.id === NRIC_CITIZEN_ID) {
                const singaporeCitizen = this.nationality_options.filter(
                    nationality => nationality.text === SINGAPORE_CITIZEN
                ).map(
                    nation => ({
                        text: nation.text,
                        value: nation.value
                    })
                )[0];

                this.registrableController.nationality = singaporeCitizen;
            }
        },

        // eslint-disable-next-line func-names
        requestAutoFillFormData: debounce(async function(id) {
            // if nominator type not select then it load data as individual
            if(this.registrableController.shareholderType?.text) {
                const user = await User.getByNRIC(id, this.registrableController.shareholderType?.text);

                if(user) this.fillNomineeRegistrableControllerData(user);
            }
        }, 500),

        fillNomineeRegistrableControllerData(user) {
            this.registrableController.nric = user.user_info?.NRIC;
            this.registrableController.user_id = user.id;
            this.registrableController.name = user?.user_info?.legal_name ?? user?.name;
            this.registrableController.email = user?.email;
            this.registrableController.dob = formatDate(user?.user_info?.DOB);
            this.registrableController.email = user?.email;
            this.registrableController.contactNumber = user?.user_info?.contact_number;
            this.registrableController.address = user?.user_info?.address;
            this.registrableController.alias = user?.individual_shareholder?.alias;

            if (this.isRegistrableControllerTypeIndividual) {
                this.registrableController.idType = {
                    id: user.user_info.id_type?.id,
                    name: user.user_info.id_type?.name
                };

                this.registrableController.nationality = {
                    text: user.user_info.nationality?.name,
                    value: user.user_info.nationality?.id
                };

                this.registrableController.dateOfExpiry = formatDate(user?.user_info?.date_of_expiry);
            }

            if(! this.isRegistrableControllerTypeIndividual) {
                this.registrableController.country = {
                    text: user?.user_info?.country?.name,
                    value: user?.user_info?.country?.id
                };

                this.registrableController.uen = user?.user_info?.uen;
                this.registrableController.statue = user?.shareholder?.statue;
                this.registrableController.registrar = user?.shareholder?.registrar;
                this.registrableController.jurisdiction = user?.shareholder?.jurisdiction;
                this.registrableController.legalForm = user?.shareholder?.legal_form ? {
                    value: user.shareholder.legal_form,
                    text: user.shareholder.legal_form
                } : null;
            }
        },

        createOrUpdate() {
            this.$refs.createRegistrableControllerValidation
                .validate()
                .then(async success => {
                    if (success) {
                        // const type = this.registrableController.shareholderType.text;

                        const data = {
                            ...this.registrableController,
                            // shareholderType: ,
                        };

                        if (! this.isRegistrableControllerTypeIndividual)
                            data.country = this.registrableController.country?.value;

                        this.$emit("createOrEdit", data);
                        this.$emit("cancel");
                    }
                });
        },
    },
};
</script>

